/**
 * @file Bidder list/detail, scan, pick toggles, and activity API helpers.
 */

import client from './client';

export const getBidders = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.bidderId) params.append('bidderId', filters.bidderId);
  if (filters.name) params.append('name', filters.name);
  if (filters.invoiceNumber) params.append('invoiceNumber', filters.invoiceNumber);
  if (filters.hasOpenInvoice) params.append('hasOpenInvoice', 'true');
  if (filters.auctionGroupId) params.append('auctionGroupId', filters.auctionGroupId);
  if (filters.loadedOut && filters.loadedOut !== 'all') params.append('loadedOut', filters.loadedOut);
  if (filters.markedPicked && filters.markedPicked !== 'all') params.append('markedPicked', filters.markedPicked);
  if (filters.scanStatus && filters.scanStatus !== 'all') params.append('scanStatus', filters.scanStatus);
  if (filters.sortKey) params.append('sortKey', filters.sortKey);
  if (filters.sortDir) params.append('sortDir', filters.sortDir);
  if (filters.limit != null) params.append('limit', String(filters.limit));
  if (filters.offset != null) params.append('offset', String(filters.offset));

  const response = await client.get(`/bidders?${params.toString()}`);
  return response.data;
};

export const getBidderById = async (id, auctionGroupId = null, options = {}) => {
  const params = new URLSearchParams();
  if (auctionGroupId) {
    params.append('auctionGroupId', auctionGroupId);
  }
  if (options.minimal) {
    params.append('minimal', '1');
  }
  const response = await client.get(`/bidders/${id}?${params.toString()}`);
  return response.data;
};

export const getBidderByInvoice = async (invoiceNumber, auctionGroupId) => {
  const params = new URLSearchParams();
  params.append('invoiceNumber', invoiceNumber);
  if (auctionGroupId) {
    params.append('auctionGroupId', String(auctionGroupId));
  }
  const response = await client.get(`/bidders/by-invoice?${params.toString()}`);
  return response.data;
};

export const getBidderLots = async (id, auctionGroupId = null) => {
  const params = new URLSearchParams();
  if (auctionGroupId) {
    params.append('auctionGroupId', auctionGroupId);
  }
  const response = await client.get(`/bidders/${id}/lots?${params.toString()}`);
  return response.data;
};

export const getBidderLotsWithScanStatus = async (id, auctionGroupId = null) => {
  const params = new URLSearchParams();
  if (auctionGroupId) {
    params.append('auctionGroupId', auctionGroupId);
  }
  const response = await client.get(`/bidders/${id}/lots-with-scan-status?${params.toString()}`);
  return response.data;
};

export const getScannedLots = async (id) => {
  const response = await client.get(`/bidders/${id}/scanned-lots`);
  return response.data;
};

export const scanLot = async (id, qrCode, lotNumber, auctionGroupId = null) => {
  const body = lotNumber ? { lotNumber } : { qrCode };
  if (auctionGroupId) {
    body.auctionGroupId = auctionGroupId;
  }
  const response = await client.post(`/bidders/${id}/scan`, body);
  return response.data;
};

/**
 * Set loaded-out status. Pass an explicit `value` so the request stays
 * idempotent — a network retry then cannot flip the flag back.
 */
/** Yard wifi dropouts should fail fast; the queue retries on its own timer. */
const PICK_TOGGLE_REQUEST = {
  timeout: 8000,
  skipNetworkRetry: true,
};

export const togglePickedStatus = async (id, auctionGroupId = null, value = undefined) => {
  const body = {};
  if (auctionGroupId) {
    body.auctionGroupId = auctionGroupId;
  }
  if (typeof value === 'boolean') {
    body.value = value;
  }
  const response = await client.post(`/bidders/${id}/toggle-picked`, body, PICK_TOGGLE_REQUEST);
  return response.data;
};

/** Set marked-picked status. See {@link togglePickedStatus} for `value`. */
export const toggleMarkedPickedStatus = async (id, auctionGroupId = null, value = undefined) => {
  const body = {};
  if (auctionGroupId) {
    body.auctionGroupId = auctionGroupId;
  }
  if (typeof value === 'boolean') {
    body.value = value;
  }
  const response = await client.post(`/bidders/${id}/toggle-marked-picked`, body, PICK_TOGGLE_REQUEST);
  return response.data;
};

export const getBidderActivity = async (id) => {
  const response = await client.get(`/bidders/${id}/activity`);
  return response.data;
};

export const getBidderNotes = async (id) => {
  const response = await client.get(`/bidders/${id}/notes`);
  return response.data;
};

export const createBidderNote = async (id, body) => {
  const response = await client.post(`/bidders/${id}/notes`, { body });
  return response.data;
};

export const deleteBidderNote = async (id, noteId) => {
  const response = await client.delete(`/bidders/${id}/notes/${noteId}`);
  return response.data;
};

export const logBidderActivity = async (id, { eventType, auctionGroupId }) => {
  const response = await client.post(`/bidders/${id}/activity`, {
    eventType,
    auctionGroupId,
  });
  return response.data;
};



